import type { AwsWafConfig } from '../types';
import { useScript } from '../utilities/use-script';
import { AWS_CAPTCHA_SCRIPT_ID } from './aws-captcha.constants';

type UseAwsWafCaptchaSdkScriptReturnType = {
  /** True once the AWS WAF CAPTCHA SDK script has finished loading. */
  hasLoaded: boolean;
};

/**
 * Injects the AWS WAF CAPTCHA SDK script once a valid configuration is available
 * and reports whether the SDK has finished loading.
 *
 * Injection and readiness are delegated to `useScript` from `usehooks-ts`, which
 * de-duplicates by `src` (with the tag keyed on {@link AWS_CAPTCHA_SCRIPT_ID})
 * and shares loading status across consumers via an internal cache; remounting
 * this hook therefore cannot append a duplicate script. The script is left in
 * place after unmount (`removeOnUnmount` defaults to false) — it is a page-level
 * dependency and does not need to be loaded twice.
 *
 * If the SDK global is already present (e.g. the script loaded on a previous
 * render before this hook mounted), `hasLoaded` reflects that synchronously.
 */
export const useAwsWafCaptchaSdkScript = (
  awsWafConfig: AwsWafConfig | null
): UseAwsWafCaptchaSdkScriptReturnType => {
  const status = useScript(awsWafConfig ? awsWafConfig.integrationUrl : null, {
    id: AWS_CAPTCHA_SCRIPT_ID,
  });

  // The SDK global is defined by the script once it executes; treat its presence
  // as "loaded" to cover the case where the script resolved before this hook
  // subscribed to the `load` event.
  const hasLoaded = status === 'ready' || Boolean(window.AwsWafCaptcha);

  return { hasLoaded };
};
